import { defineStore } from "pinia";
import axios from "axios";
import { useAuthStore } from "./authStore";

export const useUsersStore = defineStore("users", {
  state: () => ({
    users: [], // Array to store user objects fetched from the server
    status: {
      error: "",   // Holds error messages related to user actions
      success: "", // Holds success messages related to user actions
    },
    isLoading: false,      // Indicates whether a user-related request is in progress
    updateInterval: null,  // Reference to the interval for auto-updating users
  }),

  actions: {
    /**
     * Fetches the list of users from the server.
     * @returns {boolean} - Returns true if users are fetched successfully, else false.
     */
    async fetchUsers() {
      this.isLoading = true;
      this.status.error = "";
      this.status.success = "";

      const authStore = useAuthStore();

      if (!authStore.isAuthenticated) {
        this.status.error = "Du skal være logget ind for at hente brugere.";
        this.isLoading = false;
        return false;
      }

      try {
        const response = await axios.get("my-plugin/v1/users", {
          headers: {
            "X-WP-Nonce": authStore.wpNonce, // Ensures the request is secure
          },
          withCredentials: true, // Includes cookies in the request
        });

        if (response.data.success) {
          this.users = response.data.users;
          console.log("Fetched users:", this.users); // Debug log
          return true;
        } else {
          this.status.error = "Kunne ikke hente brugere. Prøv venligst igen.";
          return false;
        }
      } catch (error) {
        console.error("Error fetching users:", error); // Debug log
        await this.handleError(error);
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Updates the balances for a specific user.
     * @param {number} userId - The ID of the user whose balances are being updated.
     * @param {Array} balances - An array of balance objects to update.
     * @returns {boolean} - Returns true if the update was successful, false otherwise.
     */
    async updateUserBalances(userId, balances) {
      // Set loading state and clear previous status messages
      this.isLoading = true;
      this.status.error = "";
      this.status.success = "";
    
      const authStore = useAuthStore();
    
      // Check if the user is authenticated
      if (!authStore.isAuthenticated) {
        this.status.error = "Du skal være logget ind for at opdatere brugerbalancer.";
        this.isLoading = false;
        return false;
      }
    
      try {
        // Send POST request to update user balances
        const response = await axios.post("my-plugin/v1/update-user-balances", {
          user_id: userId,
          balances: balances,
        }, {
          headers: {
            "X-WP-Nonce": authStore.wpNonce, // Include WordPress nonce for security
          },
          withCredentials: true, // Include cookies in the request
        });
    
        // Check if the update was successful
        if (response.data.success) {
          this.status.success = "Brugerbalancer opdateret succesfuldt.";
          return true;
        } else {
          this.status.error = "Kunne ikke opdatere brugerbalancer. Prøv venligst igen.";
          return false;
        }
      } catch (error) {
        // Log the error and handle it
        console.error("Error updating user balances:", error);
        await this.handleError(error);
        return false;
      } finally {
        // Reset loading state
        this.isLoading = false;
      }
    },

    /**
     * Starts the auto-update mechanism to periodically fetch users.
     * @param {number} interval - The interval in milliseconds for auto-updating (default is 120000ms).
     */
    startAutoUpdate(interval = 120000) {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
      }
      this.updateInterval = setInterval(() => {
        this.fetchUsers();
      }, interval);
    },

    /**
     * Stops the auto-update mechanism.
     */
    stopAutoUpdate() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
    },

    /**
     * Initializes the store by fetching users and starting the auto-update if successful.
     */
    async initializeStore() {
      const success = await this.fetchUsers();
      if (success) {
        this.startAutoUpdate();
      }
    },

    /**
     * Generates initials from a user's email address.
     * @param {string} email - The user's email address.
     * @returns {string} - The generated initials (first 3 characters of the email, uppercase).
     */
    generateInitials(email) {
      if (!email) return "";
      return email.slice(0, 3).toUpperCase();
    },

    /**
     * Handles errors from Axios requests and sets appropriate error messages in the store.
     * @param {Object} error - The error object returned by Axios.
     */
    async handleError(error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const authStore = useAuthStore();
          console.log("Error response:", error.response); // Debug log
          // If unauthorized, log out the user
          if (error.response.status === 401 || error.response.status === 403) {
            await authStore.logout();
            this.status.error = "Session udløbet. Du er blevet logget ud.";
          } else {
            this.status.error = error.response.data.message || "Der opstod en fejl under hentning af brugere.";
          }
        } else if (error.request) {
          this.status.error =
            "Ingen respons fra serveren. Kontroller din internetforbindelse.";
        } else {
          this.status.error =
            "Der opstod en fejl ved forberedelse af anmodningen.";
        }
      } else {
        this.status.error = "Der opstod en uventet fejl.";
      }
    },
  },

  getters: {
    /**
     * Retrieves a user object by its unique ID.
     * @param {Object} state - The store's state.
     * @returns {Function} - A function that takes a user ID and returns the corresponding user object.
     */
    getUserById: (state) => (id) => {
      return state.users[id];
    },

    /**
     * Retrieves the events associated with a specific user.
     * @param {Object} state - The store's state.
     * @returns {Function} - A function that takes a user ID and returns an array of events.
     */
    getUserEvents: (state) => (id) => {
      const user = state.users[id];
      return user ? user.events : [];
    },

    /**
     * Retrieves the holiday balances for a specific user.
     * @param {Object} state - The store's state.
     * @returns {Function} - A function that takes a user ID and returns an object of holiday balances.
     */
    getUserHolidayBalances: (state) => (id) => {
      const user = state.users[id];
      return user ? user.holiday_balances : {};
    },

    /**
     * Retrieves the initials for a specific user.
     * @returns {Function} - A function that takes a user object and returns the initials.
     */
    getUserInitials: (state) => (user) => {
      if (!user || !user.email) return "";
      return state.generateInitials(user.email);
    },
  },
});